import React, { useRef, useState } from 'react';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography } from '@material-ui/core';
import cx from 'classnames';

import useCommonStyles from 'styles/common';
import { ApiResponse } from 'types';


interface ImageUploadBoxProps {
	value: string;
	onUpload: (filename: string) => Promise<ApiResponse>; // should return upload_url
	onUploaded: (blobname: string) => Promise<ApiResponse>; // should return image_url
	setValue: (url: string) => void;
	color?: string;
	className?: string;
	style?: React.CSSProperties;
}

const useStyles = makeStyles((theme) => ({
	content: {
		width: '100%',
		maxWidth: 280,
		height: 280,
		border: "1px solid var(--color-tertiary)",
		borderRadius: 16,
		cursor: 'pointer', 
		overflow: 'hidden',
		background: 'var(--color-white)',
	},
	innerBox : {
		flexDirection: 'column'
	},
	icon : {
		fontSize: 64,
		marginBottom: 24
	}
}));

const ImageUploadBox: React.FC<ImageUploadBoxProps> = ({ 
	className, style,
	color='var(--color-tertiary)',
	value, onUpload, onUploaded, setValue 
}) => {
	const classes = useStyles();
	const common = useCommonStyles();

	const [isUploading, setUploading] = useState(false);

	const inputRef = useRef<HTMLInputElement>(null);

	const handleClick = () => {
		inputRef.current?.click();
	};

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		try {
			const file = e.target.files?.[0];
			if (!file) return;

			// 檢查大小與副檔名（最大5MB範例）
			if (!file.type.startsWith('image/') || file.size > 10 * 1024 * 1024) {
				alert('請上傳小於10MB的圖片檔案');
				return;
			}

			setUploading(true)
			const response: ApiResponse = await onUpload(file.name);
			const uploadUrl = response.data.url;
			const blobname = response.data.blobname;

			await fetch(uploadUrl, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/octet-stream'
				},
				body: file
			});

			const imageUrl = (await onUploaded(blobname)).data.url;
			setUploading(false)

			setValue(imageUrl);
		} catch (err: any) {
			console.error(err)
			alert('Upload failed!')
			setUploading(false)
		}
	};

	return (
		<Box
			className={ cx(classes.content, common.center, className) }
			onClick={handleClick}
			style={ style }
		>
			{value ? (
				<img src={value} alt="uploaded" className={common.imgCover} />
			) : (
				<Box className={cx(common.center, classes.innerBox)}>
					<ControlPointIcon className={classes.icon} style={{ color: color }} />
					{ isUploading ? 
						<Typography style={{ color: color }}>上傳中</Typography>
						:<>
							<Typography style={{ color: color }}>點擊上傳</Typography>
							<Typography style={{ color: color }}>發票照片</Typography>
						</>
					}
				</Box>
			)}
			<input
				type="file"
				accept="image/*"
				style={{ display: 'none' }}
				ref={inputRef}
				onChange={handleFileChange}
				disabled={isUploading}
			/>
		</Box>
	);
};

export default ImageUploadBox;
